import { DateTime } from 'luxon';
import { CouchLoginResult } from '../couch-auth';
import { CouchUserInfo, NEVER_EXPIRES, UserLicenseType } from './couch-user';
import { LICENSE_RAILS } from './license-reconciler';

/**
 * The one place that turns a stored subscription expiry into a date a person is
 * shown.
 *
 * ## The problem this exists to remove
 *
 * Most of our payment providers do NOT store the provider's own date. Their
 * writers add our own 24-hour access grace first, so what sits in
 * `expiresDate_utc` is the real billing period end PLUS 24 hours. Apple is the
 * exception and stores the raw date. Which rails do which is written down once,
 * as `readStoredExpiryGraceHours` on each row of `LICENSE_RAILS`.
 *
 * That one stored value has to answer two different questions:
 *
 * - **"When does my access end?"** — the stored value, unchanged. This is what a
 *   member experiences: the last moment the app still lets them in.
 * - **"When does the provider actually charge, pause or cancel me?"** — the
 *   stored value minus that rail's grace. On Apple the two are the same moment.
 *
 * Before this module every screen decided for itself, and the same date appeared
 * a day apart on one screen three separate times while #4189 was being built.
 * Ticket #4278 is the cleanup. The rule for a new display site is short: work out
 * which of the two questions your SENTENCE asks — read the English text, not the
 * field name — and read the matching field off {@link SubscriptionEndInstants}.
 *
 * ## What this module deliberately does not do
 *
 * It does not format, because the callers genuinely differ: the client shows the
 * viewer's own local day, the server writes an email in the coach's language and
 * zone. One site is deliberately NOT routed through here for that reason:
 * `formatNoticeDate` in `server/functions/src/helpers/coach-billing-notify.ts`
 * resolves a date in whatever zone the container runs in, on purpose, because we
 * do not store the recipient's zone and pinning UTC would silently shift the date
 * printed in every existing coach email. It asks the access question and already
 * answers it correctly; do not "finish the sweep" by pointing it here.
 *
 * It does not clamp a past date to now either — a period end that has
 * already gone by is a true and useful answer, and the sites that care about it
 * already switch their WORDING rather than their date (see `accessAlreadyEnded`
 * in `cs-day-items-coach-cards.ts`). And it takes no `now`, so it stays a pure
 * function of the record.
 *
 * ## Where the rest of this is written down
 *
 * The per-rail grace table, how to tell which question a SENTENCE asks, and the
 * limit of the provider-acts answer (two Paddle writers deliberately store a
 * value that is not a period end, so the answer is an estimate for them) are all
 * in design-docs/iap-subscriptions.md, "Access-ends versus provider-acts". That
 * is the one copy; do not restate it here.
 */

/** The two questions a stored expiry can be asked, answered together. */
export interface SubscriptionEndInstants {
  /**
   * When WE stop granting access — the stored expiry, parsed as UTC.
   *
   * `undefined` when there is no date to show at all: the value is missing,
   * indefinite (`'never'`), or unparseable.
   */
  accessEndsAt: DateTime | undefined;

  /**
   * When the PROVIDER renews, charges, pauses or cancels.
   *
   * `undefined` whenever {@link accessEndsAt} is, and ALSO when this rail's grace
   * is unknowable — an unknown rail, or a coached client's mirrored grant, which
   * copies a coach's expiry without recording which store the coach pays through.
   *
   * Never guessed. A date that is silently one day wrong looks exactly like a
   * right one, so no screen would ever catch it; a missing date is something
   * somebody notices. Every display site already has wording for having no date.
   */
  providerActsAt: DateTime | undefined;
}

const NO_INSTANTS: SubscriptionEndInstants = { accessEndsAt: undefined, providerActsAt: undefined };

/**
 * Both instants for one rail's stored expiry.
 *
 * `rail` is `undefined` when the caller genuinely does not know which provider
 * wrote the value — the client's offline path has a cached expiry and no
 * `licenseType` beside it. That is an ordinary input, not an error: the access
 * answer is still exact, and only the provider answer is withheld.
 *
 * `info` supplies the blocks the rails read. Only `coachTierData` (which store a
 * coach tier came from) and `paddleData` (Paddle's own scheduled-change date) are
 * consulted; pass `{}` when neither exists.
 */
export function resolveSubscriptionEndInstants(
  rail: UserLicenseType | undefined,
  storedExpiry: string | undefined,
  info: Pick<CouchUserInfo, 'coachTierData' | 'paddleData'>,
): SubscriptionEndInstants {
  if (!storedExpiry || storedExpiry === NEVER_EXPIRES) return NO_INSTANTS;
  // parsed as UTC because the field is `_utc` by contract — without the zone
  // Luxon rebases it onto whatever zone the device or container happens to be
  // in, which moves the date for anyone west of UTC near midnight.
  const accessEndsAt = DateTime.fromISO(storedExpiry, { zone: 'utc' });
  if (!accessEndsAt.isValid) return NO_INSTANTS;

  return { accessEndsAt, providerActsAt: resolveProviderActsAt(rail, accessEndsAt, info) };
}

/**
 * Both instants for "this user's own subscription", read off their login record.
 *
 * The expiry here is the reconciler-derived top-level `licenseExpires_utc`, and
 * `licenseType` names the rail that won and therefore wrote it — which is what
 * makes the top-level value answerable at all.
 *
 * Reads the LOCAL login record on purpose. The server's `provider` field from
 * `getSubscriptionInfo` is null whenever that call fails transiently, and a rule
 * keyed on it then states something false about the member's own subscription —
 * the failure mode `subscription-card-model.ts` documents at length (#4189).
 */
export function resolveOwnSubscriptionEndInstants(
  login: Pick<CouchLoginResult, 'licenseType' | 'licenseExpires_utc' | 'coachTierData' | 'paddleData'>
    | null
    | undefined,
): SubscriptionEndInstants {
  if (!login) return NO_INSTANTS;
  return resolveSubscriptionEndInstants(login.licenseType, login.licenseExpires_utc, login);
}

/**
 * The provider's own date, or undefined when it cannot be known.
 *
 * For Paddle this PREFERS `scheduledChange.effectiveAt_utc` when one is pending.
 * That field is already the provider's date — the webhook stores Paddle's own
 * `effective_at` verbatim, and the self-service writer stores an already
 * de-graced period end — so it is both more accurate than our arithmetic and a
 * value that must never be passed through the subtraction below. Doing so would
 * take the grace off twice and report a date two days early.
 */
function resolveProviderActsAt(
  rail: UserLicenseType | undefined,
  accessEndsAt: DateTime,
  info: Pick<CouchUserInfo, 'coachTierData' | 'paddleData'>,
): DateTime | undefined {
  if (rail === undefined) return undefined;

  if (rail === UserLicenseType.Paddle) {
    const scheduled = info.paddleData?.scheduledChange?.effectiveAt_utc;
    if (scheduled) {
      const parsed = DateTime.fromISO(scheduled, { zone: 'utc' });
      // an unreadable scheduled date falls through to the arithmetic rather than
      // failing the whole answer: the stored expiry is still a usable basis.
      if (parsed.isValid) return parsed;
    }
  }

  const graceHours = LICENSE_RAILS.find(r => r.provider === rail)?.readStoredExpiryGraceHours(info);
  if (graceHours === undefined) return undefined;
  return accessEndsAt.minus({ hours: graceHours });
}
